import {
  LitElement,
  html,
  css,
  classMap,
  unsafeHTML,
  paths,
} from '@hh/global.js'

class HHProductAccordion extends LitElement {
  static properties = {
    open: { type: Boolean, reflect: true },
    summary: { type: String },
    isReviewSection: { type: Boolean },
    view: { type: String },
  }

  get details() {
    return this.renderRoot?.querySelector('.details') ?? null
  }

  get content() {
    return this.renderRoot?.querySelector('.details__content') ?? null
  }

  constructor() {
    super()
    this.open = false

    this.showAnimation = {
      keyframes: [
        { height: '0', opacity: '0' },
        { height: 'auto', opacity: '1' },
      ],
      options: { duration: 300, easing: 'linear' },
    }

    this.hideAnimation = {
      keyframes: [
        { height: 'auto', opacity: '1' },
        { height: '0', opacity: '0' },
      ],
      options: { duration: 300, easing: 'linear' },
    }
  }

  connectedCallback() {
    super.connectedCallback()
    if (this.isReviewSection == true) {
      const yotpoInstance = this.querySelector(
        '.yotpo-widget-instance'
      ).getAttribute('data-yotpo-product-id')
      document
        .querySelector(
          `.product__info-wrapper [data-yotpo-product-id="${yotpoInstance}"]`
        )
        .addEventListener('click', (e) => {
          if (
            window.innerWidth >= 990 &&
            this.getAttribute('view') === 'desktop'
          ) {
            this.show()
            window.scrollTo({
              top: this.closest('hh-product-accordion').offsetTop,
              behavior: 'smooth',
            })
          } else if (
            window.innerWidth < 990 &&
            this.getAttribute('view') === 'mobile'
          ) {
            this.show()
            window.scrollTo({
              top: this.closest('hh--product-accordion').offsetTop,
              behavior: 'smooth',
            })
          }
        })
    }
  }

  firstUpdated() {
    this.content.hidden = !this.open
    this.content.style.height = this.open ? 'auto' : 0
  }

  updated(changedProperties) {
    if (changedProperties.has('open')) {
      this.handleOpenChange()
    }
  }

  onSummaryClick(e) {
    e.preventDefault()
    this.open ? this.hide() : this.show()
  }

  show() {
    if (this.open) return
    this.open = true
    return HH.waitForEvent(this, 'hh-after-show')
  }

  hide() {
    if (!this.open) return
    this.open = false
    return HH.waitForEvent(this, 'hh-after-hide')
  }

  async handleOpenChange() {
    if (this.open) {
      this.dispatchEvent(new CustomEvent('hh-show', { bubbles: true }))
      this.details.open = true

      await HH.stopAnimations(this.content)
      this.content.hidden = false
      const { keyframes, options } = this.showAnimation
      await HH.animateTo(
        this.content,
        HH.shimKeyframesHeightAuto(keyframes, this.content.scrollHeight),
        options
      )
      this.content.style.height = 'auto'
      this.dispatchEvent(new CustomEvent('hh-after-show', { bubbles: true }))
    } else {
      this.dispatchEvent(new CustomEvent('hh-hide', { bubbles: true }))
      await HH.stopAnimations(this.content)
      const { keyframes, options } = this.hideAnimation
      await HH.animateTo(
        this.content,
        HH.shimKeyframesHeightAuto(keyframes, this.content.scrollHeight),
        options
      )
      this.content.hidden = true
      this.content.style.height = 'auto'
      this.details.open = false
      this.dispatchEvent(new CustomEvent('hh-after-hide', { bubbles: true }))
    }
  }

  render() {
    return html`
      <details
        class=${classMap({ details: true, 'details--open': this.open })}
        part="base"
      >
        ${this.summary
          ? html`<summary
              class="details__header"
              part="header"
              @click=${this.onSummaryClick}
              aria-expanded=${this.open}
              tabindex="0"
            >
              ${this.summary}
              <span class="details__header-icon" part="header-icon">
                <slot name="expand-icon">${unsafeHTML(paths.chevron)}</slot>
                <slot name="collapse-icon">${unsafeHTML(paths.chevron)}</slot>
              </span>
            </summary>`
          : html`<slot
              name="summary"
              class="details__header"
              @click=${this.onSummaryClick}
            ></slot>`}
        <div class="details__content" part="content">
          <slot></slot>
        </div>
      </details>
    `
  }

  static styles = css`
    :host {
      display: block;
      box-sizing: border-box;
      background-color: #fff;
    }

    .details {
      overflow-anchor: none;
      list-style: none;
      border-top: 1px solid #E6E6E6;
      border-bottom: none;
      padding: 0;
      background-color: #fff;
    }

    :host(:first-of-type) .details {
      border-top: none;
    }

    :host(:first-of-type) {
      border-radius: 12px 12px 0 0;
      overflow: hidden;
    }

    :host(:last-of-type) {
      border-radius: 0 0 12px 12px;
      overflow: hidden;
    }

    .details__header {
      cursor: pointer;
      text-transform: uppercase;
      list-style: none;
      font: var(--hh-body-bold-font);
      color: var(--hh-color-text);
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 24px;
    }

    .details__header::-webkit-details-marker { display: none; }

    .details__header-icon,
    .details__header-icon ::slotted([slot='expand-icon']),
    .details__header-icon ::slotted([slot='collapse-icon']) {
      width: 14px;
      display: block;
      justify-content: center;
      align-items: center;
      min-width: 14px;
    }

    .details__header-icon svg { width: 100%; height: 100%; }

    .details__content {
      font: var(--hh-content-font);
      overflow: hidden;
      margin-top: 0;
      color: var(--hh-color-text);
      display: block;
    }
    
    .details--open slot[name='expand-icon'],
    .details:not(.details--open) slot[name='collapse-icon'] {
      display: none;
    }

    [hidden] { display: none; }
  `
}

if (!customElements.get('hh-product-accordion')) {
  customElements.define('hh-product-accordion', HHProductAccordion)
}


